import { FormatListBulleted as BoardIcon, AddCircle as PlusCircle } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { ChapterBoard } from '@/features/chapters/components/board/chapter-board';
import { useCreateChapter } from '@/features/chapters/hooks/use-mutation-hooks';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { NarrativeSummary, TextSummary } from '@/lib/types';

export const Route = createFileRoute('/scenes/')({
  component: ChaptersPage,
});

function ChaptersContent() {
  const { t } = useTranslation();
  const { novel, getSummaries } = useNovelRepository();
  const selectedNovelId = novel?.id;
  const { createChapter } = useCreateChapter();

  const scenes = getSummaries<NarrativeSummary>('scenes')
    .filter(s => s.chapterId && s.mainContentId && !s.isArchive);
  const contents = getSummaries<TextSummary>('contents');

  const totalScenes = scenes.length;

  // Only count words from contents that are mainContentId of a scene assigned to a chapter and not archived
  const contentMap = Object.fromEntries(contents.map(c => [c.id, c]));
  const totalWords = scenes
    .reduce((sum, s) => sum + (contentMap[s.mainContentId!]?.wordCount || 0), 0);

  if (!selectedNovelId || !novel.id) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5">{t('entity_list.no_novel_title')}</Typography>
      </Box>
    );
  }

  return (
    <Page
      title={t('texts.title')}
      description={t('texts.description')}
      layoutMode="scrolling"
      headerSx={{ mb: 1.5 }}
      actions={
        <Button
          id="new-chapter-step"
          variant="contained"
          onClick={() => createChapter()}
          startIcon={<PlusCircle sx={{ fontSize: 18 }} />}
          sx={{ borderRadius: 2 }}
        >
          {t('editor.new_chapter')}
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%' }}>
        {/* Main Board Area */}
        <Box sx={{ flexGrow: 1 }}>
          <ChapterBoard />
        </Box>

        {/* Footer Stats */}
        <Box sx={{
          mt: 'auto',
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.default',
          zIndex: 10
        }}>
          <Stack direction="row" spacing={4}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('entity_list.scenes.total_count')}: <Box component="span" sx={{ color: 'text.primary' }}>{totalScenes}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('entity_list.scenes.total_words')}: <Box component="span" sx={{ color: 'text.primary' }}>{totalWords.toLocaleString()} {t('editor.words')}</Box>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BoardIcon sx={{ fontSize: 14 }} /> {t('entity_form.common.autosaved')}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Page>
  );
}

function ChaptersPage() {
  return (
    <AuthGuard>
      <ChaptersContent />
    </AuthGuard>
  );
}
